// IndexedDB layer for offline-first web POS (P9.1). Uses `idb`. NEVER localStorage.
// Stores: catalog (key=sku, multiEntry index 'codes'), sale_queue (key=client_txn_id), meta (key=k).
import { openDB } from "idb";

const DB_NAME = "reason-pos-offline";
const DB_VERSION = 2;

let _dbp;
function db() {
  if (!_dbp) {
    _dbp = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        if (!d.objectStoreNames.contains("catalog")) {
          const c = d.createObjectStore("catalog", { keyPath: "sku" });
          c.createIndex("codes", "codes", { multiEntry: true });
        }
        if (!d.objectStoreNames.contains("sale_queue")) {
          d.createObjectStore("sale_queue", { keyPath: "client_txn_id" });
        }
        if (!d.objectStoreNames.contains("meta")) {
          d.createObjectStore("meta", { keyPath: "k" });
        }
        if (!d.objectStoreNames.contains("cashiers")) {
          d.createObjectStore("cashiers", { keyPath: "user_id" });
        }
      },
    });
  }
  return _dbp;
}

// ── catalog ──────────────────────────────────────────────────────────────────
export async function putCatalog(products = []) {
  const d = await db();
  const tx = d.transaction("catalog", "readwrite");
  await tx.store.clear();
  for (const p of products) {
    if (!p.sku) continue;
    await tx.store.put({ ...p, codes: Array.from(new Set([...(p.codes || []), p.sku].filter(Boolean))) });
  }
  await tx.done;
  await setMeta("catalog_synced_at", new Date().toISOString());
  await setMeta("catalog_count", products.length);
}

export async function findByCode(code) {
  if (!code) return null;
  const key = String(code).trim();
  const d = await db();
  const bySku = await d.get("catalog", key);
  if (bySku) return bySku;
  return (await d.getFromIndex("catalog", "codes", key)) || null;
}

export async function catalogCount() {
  return (await db()).count("catalog");
}

// ── sale queue ────────────────────────────────────────────────────────────────
export async function enqueueSale(sale) {
  const d = await db();
  await d.put("sale_queue", { ...sale, status: "Pending", queued_at: new Date().toISOString(), attempts: 0 });
  return sale.client_txn_id;
}

export async function pendingSales() {
  const all = await (await db()).getAll("sale_queue");
  return all.filter((s) => s.status === "Pending" || s.status === "Failed");
}

export async function allQueued() {
  return (await db()).getAll("sale_queue");
}

export async function markSale(client_txn_id, patch) {
  const d = await db();
  const existing = await d.get("sale_queue", client_txn_id);
  if (existing) await d.put("sale_queue", { ...existing, ...patch });
}

export async function clearSynced() {
  const d = await db();
  const tx = d.transaction("sale_queue", "readwrite");
  for (const s of await tx.store.getAll()) {
    if (s.status === "Synced" || s.status === "Duplicate") await tx.store.delete(s.client_txn_id);
  }
  await tx.done;
}

// ── meta ──────────────────────────────────────────────────────────────────────
export async function setMeta(k, value) {
  await (await db()).put("meta", { k, value });
}
export async function getMeta(k) {
  const row = await (await db()).get("meta", k);
  return row ? row.value : null;
}

// ── cashier offline auth (P13) ────────────────────────────────────────────────
// Stores the hashed credential bundle so the lock screen authenticates offline.
async function sha256Hex(text) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function cacheCashierBundle(cashiers = []) {
  const d = await db();
  const tx = d.transaction("cashiers", "readwrite");
  await tx.store.clear();
  for (const c of cashiers) await tx.store.put(c);
  await tx.done;
  await setMeta("cashier_bundle_at", new Date().toISOString());
}

export async function getCashiers() {
  return (await db()).getAll("cashiers");
}

// Offline PIN verification using the SHA-256 (salt + pin) verifier from the bundle.
export async function verifyPinOffline(identifier, pin) {
  const id = String(identifier || "").trim().toLowerCase();
  const all = await getCashiers();
  const c = all.find((x) => x.email?.toLowerCase() === id || x.user_id === identifier);
  if (!c || !c.pin?.sha || !c.pin?.salt) return null;
  const sha = await sha256Hex(c.pin.salt + pin);
  return sha === c.pin.sha ? c : null;
}

// Offline badge (RFID/QR) lookup using the cached badge hash.
export async function findBadgeOffline(badge) {
  const hash = await sha256Hex(String(badge || "").trim());
  const all = await getCashiers();
  return all.find((c) => (c.badges || []).some((b) => b.hash === hash)) || null;
}
